// P17c — Page /fournisseurs : liste CRUD complète
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Trash2, Plus } from "lucide-react";
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";
interface Fournisseur {
  id: number;
  name: string;
  contact: string | null;
  specialite: string | null;
  email: string | null;
  telephone: string | null;
  created_at?: string;
  updated_at?: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const fournisseursApi = {
  list: () => fetchJson<Fournisseur[]>(`/v1/fournisseurs`),
  create: (data: Partial<Fournisseur>) =>
    fetchJson<Fournisseur>(`/v1/fournisseurs`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<Fournisseur>) =>
    fetchJson<Fournisseur>(`/v1/fournisseurs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) => fetchJson<void>(`/v1/fournisseurs/${id}`, { method: "DELETE" }),
};

function FournisseurModal({
  onClose,
  initial,
}: {
  onClose: () => void;
  initial?: Fournisseur;
}) {
  const t = useT();
  const { toast } = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [contact, setContact] = useState(initial?.contact ?? "");
  const [specialite, setSpecialite] = useState(initial?.specialite ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [telephone, setTelephone] = useState(initial?.telephone ?? "");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const data = {
        name: name.trim(),
        contact: contact.trim() || undefined,
        specialite: specialite.trim() || undefined,
        email: email.trim() || undefined,
        telephone: telephone.trim() || undefined,
      };
      return initial
        ? fournisseursApi.update(initial.id, data)
        : fournisseursApi.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fournisseurs"] });
      onClose();
      toast({
        title: initial ? "Fournisseur modifié" : "Fournisseur créé",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(36rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{initial ? "Modifier" : t("common.new")} fournisseur</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs">{ht("Nom *")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={ht("Ex: TOTAL GABON")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs">{ht("Contact")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder={ht("Ex: M. Dupont")}
                />
              </div>
              <div>
                <label className="text-xs">{ht("Spécialité")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={specialite}
                  onChange={(e) => setSpecialite(e.target.value)}
                  placeholder={ht("Ex: Pièces détachées")}
                />
              </div>
              <div>
                <label className="text-xs">{ht("Email")}</label>
                <input
                  type="email"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contact@exemple.com"
                />
              </div>
              <div>
                <label className="text-xs">{ht("Téléphone")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  placeholder="+241 77 00 00 00"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!name.trim() || mutation.isPending}
            >
              {mutation.isPending ? "En cours..." : initial ? "Modifier" : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ListeFournisseursPage() {
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<Fournisseur | null>(null);
  const [del, setDel] = useState<Fournisseur | null>(null);
    const { toast } = useToast();
  const qc = useQueryClient();
  const canEdit = useCan("write");

  const { data: fournisseurs, isLoading } = useQuery({
    queryKey: ["fournisseurs"],
    queryFn: () => fournisseursApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => fournisseursApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fournisseurs"] });
      toast({ title: ht("Fournisseur supprimé"), variant: "success" });
      setDel(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({
        title,
        description: msg,
        variant: "error",
        duration: 8000,
      });
      setDel(null);
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Fournisseurs")}</h1>
          <p className="text-muted-foreground">
            {t("fournisseurs.subtitle")} ({fournisseurs?.length ?? 0} {t("common.suppliers")})
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("common.new")} {t("common.supplier").toLowerCase()}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{ht("Liste des fournisseurs")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{ht("Chargement...")}</div>
          ) : !fournisseurs || fournisseurs.length === 0 ? (
            <div className="text-sm text-muted-foreground">{ht("Aucun fournisseur enregistré.")}</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">{ht("ID")}</th>
                  <th className="px-3 py-2">{ht("Nom")}</th>
                  <th className="px-3 py-2">{ht("Contact")}</th>
                  <th className="px-3 py-2">{ht("Spécialité")}</th>
                  <th className="px-3 py-2">{ht("Email")}</th>
                  <th className="px-3 py-2">{ht("Téléphone")}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {fournisseurs.map((f) => (
                  <tr key={f.id} className="group border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono">#{f.id}</td>
                    <td className="px-3 py-2 font-semibold">{f.name}</td>
                    <td className="px-3 py-2">{f.contact ?? "-"}</td>
                    <td className="px-3 py-2 text-xs">{f.specialite ?? "-"}</td>
                    <td className="px-3 py-2 text-xs">{f.email ?? "-"}</td>
                    <td className="px-3 py-2 text-xs">{f.telephone ?? "-"}</td>
                    <td className="px-3 py-2">
                      {canEdit && (
                        <div className="flex gap-1 opacity-70 hover:opacity-100">
                          <button
                            onClick={() => setEdit(f)}
                            className="rounded p-1 text-xs text-muted-foreground hover:bg-muted"
                          >{ht("Éditer")}
                          </button>
                          <button
                            onClick={() => setDel(f)}
                            className="rounded p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                            title={ht("Supprimer")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {showCreate && <FournisseurModal onClose={() => setShowCreate(false)} />}
      {edit && <FournisseurModal onClose={() => setEdit(null)} initial={edit} />}

      <ConfirmDialog
        open={del !== null}
        title={ht("Supprimer ce fournisseur ?")}
        description={`Cette action supprimera définitivement le fournisseur "${del?.name ?? ""}". Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => del && deleteMutation.mutate(del.id)}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
